import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { generateClient } from 'aws-amplify/api';
import * as mutations from '../graphql/mutations';
import useOperationalStore from '../platform/store/useOperationalStore';
import { fetchCustomers } from '../platform/engine/identitySpine';

const client = generateClient();

// The Customers workspace list. Rows are real Customer records (the identity
// spine) — not web check-ins, and not string-grouped job names — so every
// customer who has ever had a job appears, a click navigates by stable id,
// and merge repoints jobs instead of rewriting name strings everywhere.
function CustomerDashboard() {
  const navigate = useNavigate();
  const trucks = useOperationalStore(s => s.trucks);
  const [customers, setCustomers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedIds, setSelectedIds] = useState([]);
  const [isMergeMode, setIsMergeMode] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => { loadCustomers(); }, []);

  async function loadCustomers() {
    setIsLoading(true);
    const list = await fetchCustomers();
    setCustomers(list || []);
    setIsLoading(false);
  }

  // Jobs per customer from the live store: primary match by customerId,
  // legacy fallback by exact name for anything that predates the backfill.
  const jobStats = useMemo(() => {
    const stats = new Map(); // customerId -> { count, lastStart }
    const byName = new Map(); // lowercased name -> customerId
    for (const c of customers) byName.set((c.name || '').toLowerCase(), c.id);
    for (const t of trucks) {
      const id = t.customerId || byName.get((t.companyName || '').toLowerCase());
      if (!id) continue;
      const s = stats.get(id) || { count: 0, lastStart: 0 };
      s.count += 1;
      if ((t.startTime || 0) > s.lastStart) s.lastStart = t.startTime || 0;
      stats.set(id, s);
    }
    return stats;
  }, [customers, trucks]);

  const filtered = useMemo(() => {
    const q = searchTerm.toLowerCase();
    return customers
      .filter(c =>
        !q ||
        (c.name || '').toLowerCase().includes(q) ||
        (c.contactPerson || '').toLowerCase().includes(q) ||
        (c.phone || '').includes(searchTerm)
      )
      .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  }, [customers, searchTerm]);

  function toggleSelected(id) {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  }

  // Merge: keep one record, repoint every selected customer's jobs to it
  // (customerId AND the legacy companyName string), delete the others.
  async function handleMerge() {
    if (selectedIds.length < 2) { alert('Select at least 2 customers to merge.'); return; }
    const selected = customers.filter(c => selectedIds.includes(c.id));
    const names = selected.map(c => c.name).join('\n  ');
    const targetName = prompt(`Enter the correct customer name for the merge:\n  ${names}`);
    if (!targetName?.trim()) return;
    if (!window.confirm(`Merge ${selected.length} customers into "${targetName.trim()}"? All their jobs move to the one customer. Nothing about the jobs themselves changes.`)) return;

    setBusy(true);
    try {
      // Keep the record whose name matches the target if there is one.
      const kept = selected.find(c => c.name.toLowerCase() === targetName.trim().toLowerCase()) || selected[0];
      const goners = selected.filter(c => c.id !== kept.id);
      const gonerIds = new Set(goners.map(c => c.id));
      const gonerNames = new Set(goners.map(c => (c.name || '').toLowerCase()));

      const jobsToMove = trucks.filter(t =>
        gonerIds.has(t.customerId) ||
        (!t.customerId && gonerNames.has((t.companyName || '').toLowerCase()))
      );
      for (const t of jobsToMove) {
        await client.graphql({
          query: mutations.updateTruck,
          variables: { input: { id: t.id, customerId: kept.id, companyName: targetName.trim() } },
        });
      }
      if (kept.name !== targetName.trim()) {
        await client.graphql({
          query: mutations.updateCustomer,
          variables: { input: { id: kept.id, name: targetName.trim() } },
        });
      }
      for (const g of goners) {
        await client.graphql({ query: mutations.deleteCustomer, variables: { input: { id: g.id } } });
      }
      alert(`Merged into "${targetName.trim()}" — ${jobsToMove.length} jobs moved.`);
      setIsMergeMode(false);
      setSelectedIds([]);
      await loadCustomers();
      useOperationalStore.getState().fetchAll({ quiet: true });
    } catch (err) {
      console.error('Merge failed:', err);
      alert('Merge failed part-way — re-run it; already-moved jobs stay moved.');
    } finally {
      setBusy(false);
    }
  }

  // Delete: same semantics the old dashboard had — removes the customer AND
  // every one of their jobs. Kept because it's how test data gets cleaned up.
  async function handleDelete(customer) {
    if (!window.confirm(`Delete "${customer.name}" and ALL their repairs? This cannot be undone!`)) return;
    setBusy(true);
    try {
      const theirJobs = trucks.filter(t =>
        t.customerId === customer.id ||
        (!t.customerId && (t.companyName || '').toLowerCase() === (customer.name || '').toLowerCase())
      );
      for (const t of theirJobs) {
        await client.graphql({ query: mutations.deleteTruck, variables: { input: { id: t.id } } });
      }
      await client.graphql({ query: mutations.deleteCustomer, variables: { input: { id: customer.id } } });
      alert(`Deleted ${customer.name} and ${theirJobs.length} jobs.`);
      await loadCustomers();
      useOperationalStore.getState().fetchAll({ quiet: true });
    } catch (err) {
      console.error('Delete failed:', err);
      alert('Delete failed. Please try again.');
    } finally {
      setBusy(false);
    }
  }

  if (isLoading) {
    return (
      <div className="tt-page">
        <div className="tt-page-header">
          <div>
            <h1 className="tt-page-title">Customers</h1>
            <p className="tt-page-subtitle">Loading customers...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="tt-page">
      <div className="tt-page-header">
        <div>
          <h1 className="tt-page-title">Customers</h1>
          <p className="tt-page-subtitle">{filtered.length} customers</p>
        </div>
      </div>

      <div className="tt-toolbar">
        <input
          className="tt-input"
          type="text"
          placeholder="Search name, contact, or phone..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{ maxWidth: '320px' }}
        />

        <button
          className={isMergeMode ? 'tt-button tt-button--danger' : 'tt-button tt-button--primary'}
          disabled={busy}
          onClick={() => { setIsMergeMode(!isMergeMode); setSelectedIds([]); }}
        >
          {isMergeMode ? 'Cancel Merge' : 'Merge Mode'}
        </button>

        {isMergeMode && (
          <>
            <span className="tt-chip">Selected: {selectedIds.length}</span>
            <button
              className="tt-button tt-button--primary"
              onClick={handleMerge}
              disabled={busy || selectedIds.length < 2}
            >
              {busy ? 'Merging…' : 'Merge Selected'}
            </button>
          </>
        )}
      </div>

      {filtered.length === 0 ? (
        <div className="tt-card tt-list-card">
          <p className="tt-empty">No customers found.</p>
        </div>
      ) : (
        <div>
          {filtered.map((c) => {
            const isSelected = isMergeMode && selectedIds.includes(c.id);
            const stats = jobStats.get(c.id);
            return (
              <div
                key={c.id}
                className={`tt-card tt-list-card ${isSelected ? 'is-selected' : ''}`}
                onClick={() => {
                  if (busy) return;
                  if (isMergeMode) toggleSelected(c.id);
                  else navigate(`/customers/${c.id}`);
                }}
              >
                <div className="tt-card-title">{c.name}</div>
                <div className="tt-customer-details">
                  {c.contactPerson && <div><strong>Contact:</strong> {c.contactPerson}</div>}
                  {c.phone && <div><strong>Phone:</strong> {c.phone}</div>}
                  {c.email && <div><strong>Email:</strong> {c.email}</div>}
                  <div><strong>Jobs:</strong> {stats?.count || 0}</div>
                  {stats?.lastStart ? (
                    <div><strong>Last Visit:</strong> {new Date(stats.lastStart < 10_000_000_000 ? stats.lastStart * 1000 : stats.lastStart).toLocaleDateString()}</div>
                  ) : null}
                </div>

                <div className="tt-card-actions">
                  <div className="tt-meta">Click to view all repairs</div>
                  <button
                    className="tt-button tt-button--danger"
                    disabled={busy}
                    onClick={(e) => { e.stopPropagation(); handleDelete(c); }}
                  >
                    Delete
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default CustomerDashboard;
